// VoorBegunstigde.jsx  →  /voor/:token
// Begunstigde vult ALLEEN interesses (stap 2) + life event (stap 3) in
// Budget, relatie en koper blijven volledig verborgen

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Events } from "../analytics";
import { INTERESTS, LIFE_EVENTS } from "../constants";

export default function VoorBegunstigde() {
  const { token } = useParams();
  const [profiel, setProfiel] = useState(null);
  const [fout, setFout] = useState(null);
  const [laden, setLaden] = useState(true);
  const [stap, setStap] = useState(1); // 1=uitleg, 2=interesses, 3=life event, 4=bedankt
  const [interests, setInterests] = useState([]);
  const [lifeEvent, setLifeEvent] = useState(null);
  const [verzenden, setVerzenden] = useState(false);

  useEffect(() => {
    // Token is base64url-encoded JSON — direct decoderen in browser, geen fetch nodig
    try {
      const data = JSON.parse(atob(token.replace(/-/g, "+").replace(/_/g, "/")));
      if (data.status === "ingevuld") {
        setProfiel(data);
        setStap(4);
      } else {
        setProfiel(data);
        Events.BEGUNSTIGDE_GEOPEND();
      }
    } catch {
      setFout("Deze link is ongeldig of verlopen.");
    } finally {
      setLaden(false);
    }
  }, [token]);

  async function bevestigLifeEvent() {
    if (!lifeEvent || verzenden) return;
    setVerzenden(true);
    try {
      const res = await fetch("/.netlify/functions/share-link?token=" + token, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interests, lifeEvent }),
      });
      const json = await res.json();
      Events.BEGUNSTIGDE_VOLTOOID();
      // Stuur notificatie naar koper met het nieuwe token (bevat interesses + lifeEvent)
      if (json.koperEmail && json.verderUrl) {
        fetch("/.netlify/functions/notify-koper", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            koperEmail: json.koperEmail,
            naam: profiel.naam,
            token: json.token,
            verderUrl: json.verderUrl,
          }),
        }).catch(() => {});
      }
      setStap(4);
    } catch {
      setVerzenden(false);
    }
  }

  if (laden) return (
    <main className="main">
      <div className="card fi" style={{ textAlign: "center", padding: 40 }}>
        <div className="ai-spinner" style={{ margin: "0 auto 16px" }} />
        <div style={{ color: "#8A7B73", fontSize: 15 }}>Laden…</div>
      </div>
    </main>
  );

  if (fout) return (
    <main className="main">
      <div className="card fi" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, marginBottom: 8 }}>Link niet gevonden</h2>
        <p style={{ color: "#8A7B73", marginBottom: 24, lineHeight: 1.6 }}>
          Deze link is verlopen of bestaat niet meer. Links zijn 30 dagen geldig.
        </p>
        <Link to="/" style={{
          display: "inline-block", background: "#C9856A", color: "white",
          textDecoration: "none", padding: "12px 24px", borderRadius: 50, fontWeight: 600,
        }}>
          Maak zelf een profiel aan →
        </Link>
      </div>
    </main>
  );

  if (stap === 1) return (
    <main className="main">
      <div className="card fi">
        <div style={{
          background: "linear-gradient(135deg,#C9856A,#A86349)",
          borderRadius: 20, padding: 24, marginBottom: 24, textAlign: "center",
        }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>🎁</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "white", marginBottom: 6 }}>
            Iemand wil jou verrassen!
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.7 }}>
            Er wordt een cadeau voor je gezocht. Help even mee — dan wordt het nóg beter.
          </div>
        </div>
        {[
          { e: "🎯", t: "2 interesses kiezen", s: "Wat vind jij leuk?" },
          { e: "✨", t: "Iets bijzonders?", s: "Zodat het cadeau echt past" },
        ].map((item, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 0", borderBottom: i < 1 ? "1px solid #EDE3D9" : "none",
          }}>
            <div style={{
              width: 44, height: 44, background: "#FDEEE8", borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, flexShrink: 0,
            }}>{item.e}</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#2C2420" }}>{item.t}</div>
              <div style={{ fontSize: 12, color: "#8A7B73" }}>{item.s}</div>
            </div>
          </div>
        ))}
        <div style={{
          background: "#EDF3EF", border: "1px solid rgba(61,90,76,0.15)",
          borderRadius: 14, padding: "12px 16px", margin: "20px 0",
          display: "flex", gap: 10,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>🔒</span>
          <div style={{ fontSize: 13, color: "#2C2420", lineHeight: 1.6 }}>
            <strong>Het cadeau is nog een verrassing.</strong> Je weet wie het is — maar wat je krijgt, dat zie je pas als je het uitpakt.
          </div>
        </div>
        <button className="bfull bn on" onClick={() => setStap(2)}>Ik doe mee! →</button>
      </div>
    </main>
  );

  if (stap === 2) return (
    <main className="main">
      <div className="card fi">
        <div className="tag">Stap 1 van 2</div>
        <h1 className="step">Wat vind <em>jij</em> leuk?</h1>
        <p className="sub">
          Kies precies <strong>2 interesses</strong>.
          {interests.length === 2 && <span style={{ color: "#3D5A4C", fontWeight: 600 }}> ✓ Perfect!</span>}
        </p>
        <div className="chips">
          {INTERESTS.map(i => {
            const sel = interests.includes(i.id);
            return (
              <div key={i.id} className={"chip" + (sel ? " sel" : "")}
                onClick={() => {
                  const next = sel
                    ? interests.filter(x => x !== i.id)
                    : interests.length < 2 ? [...interests, i.id] : interests;
                  setInterests(next);
                }}>
                <span>{i.e}</span>{i.l}
              </div>
            );
          })}
        </div>
        <button className={"bfull bn " + (interests.length === 2 ? "on" : "off")}
          onClick={() => interests.length === 2 && setStap(3)}>
          Verder →
        </button>
      </div>
    </main>
  );

  if (stap === 3) return (
    <main className="main">
      <div className="card fi">
        <div className="tag">Stap 2 van 2</div>
        <h1 className="step">Speelt er iets <em>bijzonders</em>?</h1>
        <p className="sub">Geeft de gever nóg betere suggesties.</p>
        <div className="chips">
          {LIFE_EVENTS.map(le => (
            <div key={le.id} className={"chip" + (lifeEvent === le.id ? " sel" : "")}
              onClick={() => setLifeEvent(le.id)}>
              <span>{le.e}</span>{le.l}
            </div>
          ))}
        </div>
        <div className="brow">
          <button className="bbk" onClick={() => setStap(2)}>← Terug</button>
          <button className={"bn " + (lifeEvent ? "gr" : "off")}
            onClick={bevestigLifeEvent} disabled={verzenden}>
            {verzenden ? "Versturen…" : "Verstuur ✓"}
          </button>
        </div>
      </div>
    </main>
  );

  return (
    <main className="main">
      <div className="card fi">
        <div style={{
          background: "linear-gradient(135deg,#3D5A4C,#4a6b5a)",
          borderRadius: 20, padding: 24, marginBottom: 22, textAlign: "center",
        }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "white", marginBottom: 6 }}>
            Gelukt! Dankjewel.
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.7 }}>
            De gever krijgt nu een seintje en kan het perfecte cadeau voor je uitzoeken.
          </div>
        </div>
        <div style={{
          background: "#FDEEE8", border: "1.5px solid #EDE3D9",
          borderRadius: 20, padding: 22, marginBottom: 12, textAlign: "center",
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🎁</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#2C2420", marginBottom: 6 }}>
            Wil jij ook nooit een cadeau vergeten?
          </div>
          <div style={{ fontSize: 13, color: "#8A7B73", lineHeight: 1.6, marginBottom: 16 }}>
            Voeg iemand toe, krijg suggesties op maat en een herinnering vóór het event. Gratis, altijd.
          </div>
          <Link to="/" onClick={() => Events.VIRAL_CONVERSIE()} style={{
            display: "inline-block", background: "#C9856A", color: "white",
            textDecoration: "none", padding: "13px 28px", borderRadius: 50,
            fontWeight: 700, fontSize: 15, boxShadow: "0 4px 16px rgba(201,133,106,0.3)",
          }}>
            Start gratis met Givn →
          </Link>
        </div>
        <div style={{ textAlign: "center", fontSize: 12, color: "#8A7B73" }}>
          Gemaakt met <a href="https://givn.to" style={{ color: "#C9856A", textDecoration: "none" }}>givn.to</a>
        </div>
      </div>
    </main>
  );
}
